/**
 * The shared pieces every screen is built from.
 *
 * Hand-written rather than pulled from a component library, so the shop gets its
 * own look and every layout rule stays direction-neutral.
 */
import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type InputHTMLAttributes,
  type ButtonHTMLAttributes,
} from 'react';
import { useT } from '../i18n/index.js';
import { parseMinor, formatMinor } from '@pos/shared';

// --- buttons -----------------------------------------------------------------

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  busy?: boolean;
};

export function Button({ variant = 'default', size = 'md', block, busy, className = '', children, ...rest }: BtnProps) {
  return (
    <button
      className={`btn ${variant === 'default' ? '' : variant} ${size === 'md' ? '' : size} ${block ? 'block' : ''} ${className}`}
      disabled={rest.disabled || busy}
      {...rest}
    >
      {busy ? <span className="spin" aria-hidden /> : null}
      {children}
    </button>
  );
}

// --- fields ------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  // When the field is one box, its label and its hint are tied to it, so a tap
  // on the label reaches the box and a screen reader reads both out.
  const auto = useId();
  const single =
    isValidElement<{ id?: string; 'aria-describedby'?: string }>(children) &&
    typeof children.type === 'string' &&
    ['input', 'select', 'textarea'].includes(children.type);
  const id = single ? (children.props.id ?? auto) : undefined;
  const note = error || hint ? `${auto}-note` : undefined;
  const box = single
    ? cloneElement(children, { id, 'aria-describedby': children.props['aria-describedby'] ?? note })
    : children;
  return (
    <div className="field">
      {label ? <label htmlFor={id}>{label}</label> : null}
      {box}
      {error ? (
        <div className="err" id={note} role="alert">{error}</div>
      ) : hint ? (
        <div className="hint" id={note}>{hint}</div>
      ) : null}
    </div>
  );
}

type MoneyProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  /** Value in minor units (agorot). */
  value: number;
  onValue: (minor: number) => void;
  decimals?: number;
};

/**
 * A money box. The cashier types 12.50 and the rest of the system only ever sees
 * 1250. Arabic-Indic digits are accepted too.
 */
export function MoneyInput({ value, onValue, decimals = 2, ...rest }: MoneyProps) {
  const [text, setText] = useState(() => (value ? formatMinor(value, decimals) : ''));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(value ? formatMinor(value, decimals) : '');
  }, [value, decimals]);

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      className={`tnum ${rest.className ?? ''}`}
      value={text}
      onFocus={(e) => {
        focused.current = true;
        e.currentTarget.select();
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        focused.current = false;
        setText(value ? formatMinor(value, decimals) : '');
        rest.onBlur?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        try {
          onValue(parseMinor(raw, decimals));
        } catch {
          /* half-typed: keep the old value until it reads as a number */
        }
      }}
    />
  );
}

// --- modal -------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const t = useT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <h2>{title}</h2>
          <Button variant="ghost" className="icon" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </Button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/** A destructive action always says what will happen before it happens. */
export function Confirm({
  title,
  message,
  confirmLabel,
  danger,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{message}</p>
    </Modal>
  );
}

// --- states ------------------------------------------------------------------

export function Empty({ icon, title, hint, action }: { icon?: string; title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon ? <div className="big" aria-hidden>{icon}</div> : null}
      <h3>{title}</h3>
      {hint ? <p>{hint}</p> : null}
      {action}
    </div>
  );
}

export function Loading({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="loading">
      <div className="spin" />
      <div className="small">{label ?? t('common.loading')}</div>
    </div>
  );
}

export function Pill({ kind, children }: { kind?: 'ok' | 'warn' | 'danger' | 'info' | 'accent'; children: ReactNode }) {
  return <span className={`pill ${kind ?? ''}`}>{children}</span>;
}

export function Swatch({ hex }: { hex?: string | null }) {
  if (!hex) return null;
  return <span className="swatch" style={{ background: hex }} aria-hidden />;
}

// --- tabs --------------------------------------------------------------------

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number }[];
  active: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          className={`tab ${active === tab.key ? 'active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.count !== undefined ? <span className="tab-count">{tab.count}</span> : null}
        </button>
      ))}
    </div>
  );
}
